import { css, SerializedStyles } from '@emotion/react';

import { UIColorPalette } from './ColorPalette';

import { ageString } from '@mythos/game/Utility';
import invariant from 'invariant';
import { match } from 'ts-pattern';
import Symbols, { SymbolView } from './Symbols';

const CardTypeSymbolStyles = {
  symbol: css({
    height: 14,
    width: 'auto',
    // marginRight: 1,
    verticalAlign: 'end',
    marginBottom: -3,
    marginRight: -1,
  }),
  symbolGrayScale: css({
    filter: 'brightness(1.5) grayscale(100%)',
  }),
  container: css({
    whiteSpace: 'nowrap',
  }),
};

function PerAgePill(props: {
  values: [number, number, number];
  currentAge?: number;
}) {
  return (
    <div css={PerAgePillStyles.container}>
      {props.values.map((value, index) => {
        const isHighlighted = props.currentAge === index + 1;
        const isDimmed = props.currentAge !== undefined && !isHighlighted;
        return (
          <div
            key={index}
            css={[
              PerAgePillStyles.column,
              isHighlighted ? PerAgePillStyles.highlightedColumn : undefined,
              isDimmed ? PerAgePillStyles.dimmedColumn : undefined,
            ]}
          >
            <span css={PerAgePillStyles.columnAge}>{ageString(index + 1)}</span>
            <span css={PerAgePillStyles.columnValue}>{value}</span>
          </div>
        );
      })}
    </div>
  );
}
const PerAgePillStyles = {
  container: css({
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'stretch',

    borderRadius: 4,
    backgroundColor: UIColorPalette.perAgePillBackground,
    background: UIColorPalette.perAgePillGradient,
    height: 14,
    width: 36,

    verticalAlign: 'bottom',
    marginBottom: -0,
  }),
  column: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: 12,
  }),
  columnAge: css({
    fontSize: 4,
    textShadow: '0px 0px 1px black',
    marginBottom: -1.5,
  }),
  columnValue: css({
    fontSize: 8,
    fontWeight: 700,
    textShadow: '0px 0px 1px black',
  }),
  highlightedColumn: css({
    color: UIColorPalette.perAgePillHighlight,
  }),
  dimmedColumn: css({}),
};

type SymbolRenderConfig = {
  symbol: Symbols;
  filter?: string;
};

function sc(symbol: Symbols, filter?: string): SymbolRenderConfig {
  return filter ? { symbol, filter } : { symbol };
}

const SignatureToSymbol: Record<string, SymbolRenderConfig> = {
  GOLD: sc(Symbols.GOLD),
  MILITARY: sc(Symbols.MILITARY),
  FAVOR: sc(Symbols.FAVOR),
  TRIBUTE: sc(Symbols.TOKEN_TRIBUTE, 'invert(100%)'),

  MILITARY_WHITE_BORDER: sc(Symbols.MILITARY_WHITE_BORDER),

  TOWN: sc(Symbols.BASE_GOLD),
  FORT: sc(Symbols.BASE_MILITARY),

  DERION: sc(Symbols.CARD_AFFINITY_DERION),
  TYROS: sc(Symbols.CARD_AFFINITY_TYROS),
  ZEONA: sc(Symbols.CARD_AFFINITY_ZEONA),

  DERION_LIGHT: sc(Symbols.CARD_AFFINITY_DERION),
  TYROS_LIGHT: sc(Symbols.CARD_AFFINITY_TYROS),
  ZEONA_LIGHT: sc(Symbols.CARD_AFFINITY_ZEONA),

  BASIC: sc(Symbols.CARD_AFFINITY_BASIC),
  LEADER: sc(Symbols.CARD_AFFINITY_LEADER),

  UNIT: sc(Symbols.CARD_TYPE_UNIT),
  BUILDING: sc(Symbols.CARD_TYPE_BUILDING),
  EVENT: sc(Symbols.CARD_TYPE_EVENT),

  UNIT_LIGHT: sc(Symbols.CARD_TYPE_UNIT),
  BUILDING_LIGHT: sc(Symbols.CARD_TYPE_BUILDING),
  EVENT_LIGHT: sc(Symbols.CARD_TYPE_EVENT),

  PRODUCTION: sc(Symbols.EFFECT_PRODUCTION),
  UPGRADE: sc(Symbols.UPGRADE),
};

export type PerAgePillOptions =
  | {
      display: 'all-values';
      values: [number, number, number];
      currentAge?: number;
    }
  | {
      display: 'single-value';
      values: [number, number, number];
      currentAge: number;
    };

var convert_signatures_to_symbols = function (
  content_string: string,
  agePillOptions?: PerAgePillOptions,
  symbolStyles?: SerializedStyles,
) {
  var content: React.ReactNode[] = [];
  var last = 0;
  // Match: $WORD for symbols, X for age pills, or actual newline characters
  const regex = /\$(\w+)|\b(X)\b|(\\n)/g;
  content_string.replace(regex, (matchStr, p1, p2, p3, offset, string) => {
    if (offset !== last) {
      content.push(string.slice(last, offset));
    }
    last = offset + matchStr.length;

    if (p1) {
      const config = SignatureToSymbol[p1];
      if (!config) {
        console.warn('no symbol for', p1);
      }
      content.push(
        <span key={content.length} css={CardTypeSymbolStyles.container}>
          {config ? (
            <SymbolView
              symbol={config.symbol}
              css={[CardTypeSymbolStyles.symbol, symbolStyles]}
              style={config.filter ? { filter: config.filter } : undefined}
            />
          ) : (
            '❓️'
          )}
        </span>,
      );
    } else if (p2) {
      invariant(agePillOptions, 'missing age pill options');
      content.push(
        match(agePillOptions)
          .with({ display: 'all-values' }, (x) => {
            return <PerAgePill key={content.length} {...x} />;
          })
          .with({ display: 'single-value' }, (x) => {
            return (
              <span
                css={PerAgePillStyles.highlightedColumn}
                key={content.length}
              >
                {x.values[x.currentAge - 1]}
              </span>
            );
          })
          .exhaustive(),
      );
    } else if (p3) {
      content.push(<br key={content.length} />);
    }

    return '';
  });
  if (content_string.length !== last) {
    content.push(content_string.slice(last));
  }
  return content;
};

interface Props {
  content: string;
  perAgePillOptions?: PerAgePillOptions;

  style?: React.CSSProperties;
  className?: string;

  symbolStyle?: SerializedStyles;
}

export default function TextWithSymbols(props: Props) {
  var converted_content = convert_signatures_to_symbols(
    props.content,
    props.perAgePillOptions,
    props.symbolStyle,
  );
  return (
    <span className={props.className} style={props.style}>
      {converted_content}
    </span>
  );
}
